import React from 'react';
import { useSelector } from 'react-redux';
import { RootState, GameFormat } from '../store';
import { Tab } from '../App';
import { GraphPanel } from './GraphPanel';
import { MiniCalendar } from './MiniCalendar';
import { useT } from '../lib/i18n';

interface Props { onNavigate: (t: Tab) => void; }

export function Dashboard({ onNavigate }: Props) {
  const sessions = useSelector((s: RootState) => s.sessions.list);
  const settings = useSelector((s: RootState) => s.settings);
  const { t } = useT();

  const totalProfit    = sessions.reduce((a, s) => a + s.netProfit, 0);
  const totalSessions  = sessions.length;
  const wins           = sessions.filter(s => s.netProfit > 0).length;
  const winRate        = totalSessions > 0 ? (wins / totalSessions) * 100 : 0;
  const totalInvested  = sessions.reduce((a, s) => a + s.totalInvested, 0);
  const roi            = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;
  const currentBankroll = settings.initialBankroll + totalProfit;
  const best  = sessions.length > 0 ? Math.max(...sessions.map(s => s.netProfit)) : 0;
  const worst = sessions.length > 0 ? Math.min(...sessions.map(s => s.netProfit)) : 0;

  const formatStats: Record<string, { invested: number; returned: number; count: number }> = {};
  for (const s of sessions) {
    if (s.type === 'tournament' || s.type === 'multi') {
      for (const tr of s.tournaments) {
        const f = tr.format as GameFormat;
        if (!formatStats[f]) formatStats[f] = { invested: 0, returned: 0, count: 0 };
        formatStats[f].invested  += tr.buyIn + tr.rebuys * tr.buyIn + tr.addon;
        formatStats[f].returned  += tr.prize + tr.bountiesWon;
        formatStats[f].count++;
      }
    }
  }
  const topFormats = Object.entries(formatStats)
    .map(([fmt, d]) => ({ fmt, roi: d.invested > 0 ? ((d.returned - d.invested) / d.invested) * 100 : 0, count: d.count, profit: d.returned - d.invested }))
    .sort((a, b) => b.count - a.count).slice(0, 4);

  const recent = sessions.slice(0, 5);

  const statCards = [
    { labelKey: 'dashboard.bankroll',      value: `$${currentBankroll.toFixed(2)}`, cls: '' },
    { labelKey: 'dashboard.netProfit',     value: `${totalProfit >= 0 ? '+' : ''}$${totalProfit.toFixed(2)}`, cls: totalProfit >= 0 ? 'pos' : 'neg' },
    { labelKey: 'dashboard.winRate',       value: `${winRate.toFixed(0)}%`, cls: winRate >= 50 ? 'pos' : 'neg' },
    { labelKey: 'dashboard.totalRoi',      value: `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`, cls: roi >= 0 ? 'pos' : 'neg' },
    { labelKey: 'dashboard.sessions',      value: totalSessions, cls: '' },
    { labelKey: 'dashboard.bestSession',   value: `+$${best.toFixed(2)}`, cls: 'pos' },
    { labelKey: 'dashboard.worstSession',  value: `$${worst.toFixed(2)}`, cls: 'neg' },
    { labelKey: 'dashboard.totalInvested', value: `$${totalInvested.toFixed(2)}`, cls: '' },
  ];

  if (totalSessions === 0) {
    return (
      <div className="empty-state">
        <div className="empty-suit">◈</div>
        <h2>{t('dashboard.noSessions')}</h2>
        <p>{t('dashboard.firstSession')}</p>
        <button className="btn btn-primary" onClick={() => onNavigate('new')}>+ {t('nav.newSession')}</button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="stat-grid">
        {statCards.map(c => (
          <div key={c.labelKey} className="stat-card">
            <span className="stat-label">{t(c.labelKey)}</span>
            <span className={`stat-val ${c.cls}`}>{c.value}</span>
          </div>
        ))}
      </div>

      <div className="dashboard-main">
        <div className="dashboard-graph"><GraphPanel /></div>
        <div className="dashboard-side"><MiniCalendar sessions={sessions} /></div>
      </div>

      <div className="dashboard-bottom">
        {topFormats.length > 0 && (
          <div className="panel roi-panel">
            <div className="panel-title">{t('dashboard.roiByFormat')}</div>
            <div className="roi-list">
              {topFormats.map(f => (
                <div key={f.fmt} className="roi-row">
                  <span className="roi-fmt">{f.fmt}</span>
                  <span className="roi-count">{f.count} {t('nav.sessions').toLowerCase()}</span>
                  <span className={`roi-profit ${f.profit >= 0 ? 'pos' : 'neg'}`}>{f.profit >= 0 ? '+' : ''}${f.profit.toFixed(2)}</span>
                  <div className="roi-bar-wrap">
                    <div className={`roi-bar ${f.roi >= 0 ? 'pos' : 'neg'}`} style={{ width: `${Math.min(Math.abs(f.roi), 100)}%` }} />
                  </div>
                  <span className={`roi-pct ${f.roi >= 0 ? 'pos' : 'neg'}`}>{f.roi >= 0 ? '+' : ''}{f.roi.toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="panel recent-panel">
          <div className="panel-title">{t('dashboard.recentSessions')}</div>
          {recent.map(s => (
            <div key={s.id} className={`recent-row ${s.netProfit >= 0 ? 'win' : 'loss'}`}>
              <span className="recent-date">{s.date}</span>
              <span className="recent-type">{s.type === 'cash' ? t('sessionsTable.cash') : s.type === 'multi' ? `${s.tournaments.length}T` : s.tournaments[0]?.format || 'MTT'}</span>
              <span className="recent-platform">{s.platform}</span>
              <span className={`recent-profit ${s.netProfit >= 0 ? 'pos' : 'neg'}`}>{s.netProfit >= 0 ? '+' : ''}${s.netProfit.toFixed(2)}</span>
            </div>
          ))}
          <button className="see-all-btn" onClick={() => onNavigate('sessions')}>{t('dashboard.viewAll')}</button>
        </div>
      </div>
    </div>
  );
}
